import { useState, useEffect } from 'react';
import { useAppState } from '../contexts/AppStateContext';
import api from '../utils/axiosConfig';

const ZoneCreateModal = ({ isOpen, onClose, onSave, isLoading }) => {
  const { currentEvent } = useAppState();
  const [zoneData, setZoneData] = useState({
    name: '',
    code: '',
    ticket_types: [],
    single_checkin: false,
    activation_start: '',
    activation_end: ''
  });
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loadingTicketTypes, setLoadingTicketTypes] = useState(false);

  // Fetch ticket types when modal opens
  useEffect(() => {
    if (isOpen && currentEvent?.id) {
      fetchTicketTypes();
      setZoneData({
        name: '',
        code: '',
        ticket_types: [],
        single_checkin: false,
        activation_start: '',
        activation_end: ''
      });
    }
  }, [isOpen, currentEvent?.id]);

  const fetchTicketTypes = async () => {
    setLoadingTicketTypes(true);
    try {
      const response = await api.get(`/events/${currentEvent.id}/tickets/`);
      setTicketTypes(response.data);
    } catch (error) {
      console.error('Error fetching ticket types:', error);
    } finally {
      setLoadingTicketTypes(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setZoneData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    
    if (value && name === 'activation_start') {
      // Set time to 12:00 AM and format for API
      const date = new Date(value + 'T00:00');
      if (!isNaN(date.getTime())) {
        setZoneData(prev => ({
          ...prev,
          [name]: value // Keep the display value as YYYY-MM-DD
        }));
      }
    } else if (value && name === 'activation_end') {
      // Set time to 11:59 PM and format for API
      const date = new Date(value + 'T23:59');
      if (!isNaN(date.getTime())) {
        setZoneData(prev => ({
          ...prev,
          [name]: value // Keep the display value as YYYY-MM-DD
        }));
      }
    } else {
      // Handle clearing the field
      setZoneData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleTicketTypeChange = (ticketTypeId) => {
    setZoneData(prev => ({
      ...prev,
      ticket_types: prev.ticket_types.includes(ticketTypeId)
        ? prev.ticket_types.filter(id => id !== ticketTypeId)
        : [...prev.ticket_types, ticketTypeId]
    }));
  };

  const handleSave = () => {
    // Convert date values to ISO format for API
    const apiData = {
      ...zoneData,
      activation_start: zoneData.activation_start ? new Date(zoneData.activation_start + 'T00:00').toISOString() : '',
      activation_end: zoneData.activation_end ? new Date(zoneData.activation_end + 'T23:59').toISOString() : ''
    };
    onSave(apiData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Create New Zone</h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700"
            disabled={isLoading}
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Zone Name</label>
            <input
              type="text"
              name="name"
              value={zoneData.name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border p-2 border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="e.g., VIP Area, General Admission"
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Zone Code</label>
            <input
              type="text"
              name="code"
              value={zoneData.code}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border p-2 border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="e.g., VIP, GA"
              disabled={isLoading}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Activation Start Date</label>
              <input
                type="date"
                name="activation_start"
                value={zoneData.activation_start}
                onChange={handleDateChange}
                className="mt-1 block w-full rounded-md border p-2 border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                disabled={isLoading}
              />
              <p className="text-xs text-gray-500 mt-1">
                When this zone becomes active (optional)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Activation End Date</label>
              <input
                type="date"
                name="activation_end"
                value={zoneData.activation_end}
                onChange={handleDateChange}
                className="mt-1 block w-full rounded-md border p-2 border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                disabled={isLoading}
              />
              <p className="text-xs text-gray-500 mt-1">
                When this zone becomes inactive (optional)
              </p>
            </div>
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="single_checkin"
                checked={zoneData.single_checkin}
                onChange={handleChange}
                className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                disabled={isLoading}
              />
              <span className="text-sm font-medium text-gray-700">Single Check-in Only</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              If enabled, only one check-in per ticket for this zone is allowed
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Allowed Ticket Types
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Leave empty to allow all ticket types for this zone
            </p>
            
            {loadingTicketTypes ? (
              <div className="text-center py-4">
                <span className="text-gray-500">Loading ticket types...</span>
              </div>
            ) : ticketTypes.length === 0 ? (
              <div className="text-center py-4">
                <span className="text-gray-500">No ticket types found</span>
              </div>
            ) : (
              <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto">
                {ticketTypes.map(ticketType => (
                  <label key={ticketType.id} className="flex items-center space-x-2 py-1">
                    <input
                      type="checkbox"
                      checked={zoneData.ticket_types.includes(ticketType.id)}
                      onChange={() => handleTicketTypeChange(ticketType.id)}
                      className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      disabled={isLoading}
                    />
                    <span className="text-sm text-gray-700">{ticketType.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end mt-6 space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : (
              'Create Zone'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ZoneCreateModal;
